import { useEffect, useMemo, useRef, useState } from 'react';
import {
  addDays,
  differenceInCalendarDays,
  eachMonthOfInterval,
  endOfDay,
  format,
  max as maxDate,
  min as minDate,
  parseISO,
  startOfDay,
} from 'date-fns';
import { de } from 'date-fns/locale';
import { Sun } from 'lucide-react';
import { CenteredSpinner, EmptyState, ErrorState } from '@/components/ui';
import { cn } from '@/lib/format';
import { useTasks } from '@/lib/queries';

/**
 * Holiday Tracker — a team-availability calendar.
 *
 * The team already records absences as tasks in the "🌞 Holiday Tracker"
 * project: one task per absence, the person's name at the START of the title
 * ("Anneke - Urlaub", "Rebecca @Amsterdam WORKATION") and the date range in
 * start_at/due_at. This never rendered usefully in the old app; here it becomes
 * a proper timeline — people down the side, months across the top, a bar per
 * absence — so you can see at a glance who is off when.
 *
 * The project id is the migrated "Holiday Tracker" project. If the team ever
 * renames/moves it, this is the one line to change.
 */
const HOLIDAY_PROJECT_ID = 36;

// Minimum "natural" sizes for the timeline. The render code stretches these
// to fill the viewport (see the ResizeObserver in the component below): the
// per-day pixel width grows so the full absence range spans the visible area
// instead of leaving a big empty gutter on the right; the per-row pixel height
// grows so a handful of people don't leave a huge empty band under the last
// row. Caps stop things looking grotesque when there are very few people or
// a very narrow date range.
const DAY_PX_MIN = 5;
const DAY_PX_MAX = 24;
const ROW_H_MIN = 34;
const ROW_H_MAX = 72;
const HEADER_H = 28; // matches the `h-7` month-row height
const LABEL_W = 160;

interface Absence {
  id: number;
  person: string;
  label: string;
  start: Date;
  end: Date;
}

/** Person is the leading token of the title, before " - ", " @", or the first space. */
function parsePerson(title: string): { person: string; label: string } {
  const t = title.trim();
  const sep = t.search(/\s[-@]|\s/);
  if (sep === -1) return { person: t, label: '' };
  // Prefer an explicit " - " / " @" separator; else fall back to first word.
  const dashOrAt = t.search(/\s[-@]\s?/);
  const cut = dashOrAt !== -1 ? dashOrAt : t.indexOf(' ');
  return { person: t.slice(0, cut).trim(), label: t.slice(cut).replace(/^\s*[-@]\s*/, '').trim() };
}

export function HolidayPage() {
  const { data, isPending, error, refetch } = useTasks({
    projectId: HOLIDAY_PROJECT_ID,
    perPage: 500,
    sort: 'due_date',
  });

  const absences = useMemo<Absence[]>(() => {
    const out: Absence[] = [];
    for (const t of data?.items ?? []) {
      const startIso = t.startDate ?? t.dueDate;
      if (!startIso) continue; // can't place an absence with no date
      const start = startOfDay(parseISO(startIso));
      const end = startOfDay(parseISO(t.endDate ?? t.dueDate ?? startIso));
      const { person, label } = parsePerson(t.title);
      out.push({ id: t.id, person, label, start, end: end < start ? start : end });
    }
    return out;
  }, [data]);

  const byPerson = useMemo(() => {
    const m = new Map<string, Absence[]>();
    for (const a of absences) {
      const list = m.get(a.person);
      if (list) list.push(a);
      else m.set(a.person, [a]);
    }
    // Sort people alphabetically; their absences by start date.
    return [...m.entries()]
      .map(([person, list]) => [person, list.sort((x, y) => +x.start - +y.start)] as const)
      .sort((a, b) => a[0].localeCompare(b[0]));
  }, [absences]);

  const range = useMemo(() => {
    if (absences.length === 0) return null;
    const start = startOfDay(minDate(absences.map((a) => a.start)));
    const end = endOfDay(maxDate(absences.map((a) => a.end)));
    return { start, end, totalDays: differenceInCalendarDays(end, start) + 1 };
  }, [absences]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [dayPx, setDayPx] = useState(DAY_PX_MIN);
  const [rowH, setRowH] = useState(ROW_H_MIN);

  const totalDays = range?.totalDays ?? 0;
  const numPeople = byPerson.length;

  // Stretch day-width and row-height so the timeline fills the visible area.
  // We recompute on every viewport resize; when there are lots of people or a
  // long date range we bottom-out at the natural minimums (so the layout
  // stays scrollable rather than squashing content).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || totalDays === 0 || numPeople === 0) return;
    const compute = () => {
      const availableWidth = Math.max(0, el.clientWidth - LABEL_W);
      const nextDayPx = Math.min(
        DAY_PX_MAX,
        Math.max(DAY_PX_MIN, availableWidth / totalDays),
      );
      const availableHeight = Math.max(0, el.clientHeight - HEADER_H);
      const nextRowH = Math.min(
        ROW_H_MAX,
        Math.max(ROW_H_MIN, availableHeight / numPeople),
      );
      setDayPx(nextDayPx);
      setRowH(nextRowH);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [totalDays, numPeople]);

  if (isPending) return <CenteredSpinner label="Urlaubsplan wird geladen…" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!range || byPerson.length === 0) {
    return (
      <EmptyState
        icon={<Sun />}
        title="Kein Urlaub eingetragen"
        description="Abwesenheiten aus dem Projekt „Holiday Tracker“ erscheinen hier als Team-Zeitleiste."
      />
    );
  }

  const { start } = range;
  const months = eachMonthOfInterval({ start, end: addDays(start, totalDays) });
  const today = startOfDay(new Date());
  const todayOffset = differenceInCalendarDays(today, start);
  const width = totalDays * dayPx;
  // Bar height scales with row height (row = 34 → bar = 18, row = 72 → bar = 44).
  const barH = Math.min(48, Math.max(18, rowH - 16));

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center gap-2 border-b line surface-card px-5 py-3">
        <Sun className="size-4 text-amber-500" aria-hidden />
        <div>
          <h1 className="text-base font-semibold text-strong">Urlaubsplan</h1>
          <p className="text-xs text-muted">
            {byPerson.length} Personen · {absences.length} Einträge
          </p>
        </div>
      </header>

      <div ref={scrollRef} className="scroll-thin min-h-0 flex-1 overflow-auto">
        <div className="relative" style={{ width: width + LABEL_W }}>
          {/* Month header */}
          <div className="sticky top-0 z-20 flex border-b line surface-raised" style={{ marginLeft: LABEL_W }}>
            {months.map((m) => {
              const offset = differenceInCalendarDays(m, start);
              const daysInMonth = differenceInCalendarDays(addDays(m, 32), m); // approx, fine for width
              return (
                <div
                  key={m.toISOString()}
                  className="shrink-0 border-r line px-2 py-1.5 text-[11px] font-medium text-muted"
                  style={{ position: 'absolute', left: offset * dayPx, width: daysInMonth * dayPx }}
                >
                  {format(m, 'MMM yyyy', { locale: de })}
                </div>
              );
            })}
            <div className="h-7" />
          </div>

          {/* Today line */}
          {todayOffset >= 0 && todayOffset <= totalDays && (
            <div
              className="pointer-events-none absolute bottom-0 top-7 z-10 w-px bg-brand-500"
              style={{ left: LABEL_W + todayOffset * dayPx }}
              aria-hidden
            />
          )}

          {/* One row per person */}
          {byPerson.map(([person, list], i) => (
            <div
              key={person}
              className={cn('relative flex items-center', i % 2 ? 'surface-card' : 'surface-raised/40')}
              style={{ height: rowH }}
            >
              <div
                className="sticky left-0 z-10 flex h-full shrink-0 items-center border-r line surface-card px-3 text-[13px] font-medium text-strong"
                style={{ width: LABEL_W }}
              >
                <span className="truncate">{person}</span>
              </div>
              <div className="relative h-full flex-1">
                {list.map((a) => {
                  const left = differenceInCalendarDays(a.start, start) * dayPx;
                  const w = Math.max(dayPx, (differenceInCalendarDays(a.end, a.start) + 1) * dayPx);
                  return (
                    <div
                      key={a.id}
                      className="absolute top-1/2 flex -translate-y-1/2 items-center rounded-full bg-amber-400/90 px-2 text-[11px] font-medium text-amber-950 shadow-sm"
                      style={{ left, minWidth: w, height: barH }}
                      title={`${person}${a.label ? ` — ${a.label}` : ''}: ${format(a.start, 'd. MMM', { locale: de })}${
                        +a.end !== +a.start ? ` – ${format(a.end, 'd. MMM yyyy', { locale: de })}` : ''
                      }`}
                    >
                      <span className="truncate">{a.label || 'Urlaub'}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
